/* src/pages/Login.jsx
 * Login page
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collectAttribution } from '../utils/attribution';
import { fireSignupConversion } from '../utils/marketingTracking';
import { resolvePostAuthPath } from '../utils/postAuthRedirect';
import '../styles/auth.css';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const { login, loginWithGoogle, resetPassword, currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const redirectPath = resolvePostAuthPath({
    redirectParam: searchParams.get('redirect'),
    fromLocation: location.state?.from,
  });

  // Check if there's a pending invite token
  useEffect(() => {
    if (currentUser) {
      const pendingToken = sessionStorage.getItem('pendingInviteToken');
      if (pendingToken) {
        sessionStorage.removeItem('pendingInviteToken');
        navigate(`/accept-invite?token=${pendingToken}`);
      } else if (redirectPath) {
        navigate(redirectPath);
      }
    }
  }, [currentUser, navigate, redirectPath]);

  // Prefill the invited address when arriving from the accept-invite page
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('pendingInviteContext');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.invitedEmail) setEmail(parsed.invitedEmail);
      }
    } catch {
      // malformed sessionStorage — leave the email field blank
    }
  }, []);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'gocta.ai - Log in';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  // Store-only: capture attribution for first-time Google signups via /login.
  useEffect(() => {
    collectAttribution();
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userCredential = await login(email, password);
      
      // Check if there's a pending invite
      const pendingToken = sessionStorage.getItem('pendingInviteToken');
      if (pendingToken) {
        sessionStorage.removeItem('pendingInviteToken');
        navigate(`/accept-invite?token=${pendingToken}`);
        return;
      }
      
      // Check email verification (only for normal login, not invite flow)
      if (!userCredential.user.emailVerified) {
        navigate('/verify-email');
      } else {
        // Check redirect path or go to dashboard
        if (redirectPath) {
          navigate(redirectPath);
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err) {
      // Friendly error messages instead of raw Firebase errors
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Invalid email or password. Please try again.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again in a few minutes or reset your password.');
      } else if (err.code === 'auth/user-disabled') {
        setError('This account has been disabled. Please contact support.');
      } else {
        setError('Failed to log in. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleLogin() {
    setError('');
    setLoading(true);

    try {
      const { userCredential, isNewUser } = await loginWithGoogle(collectAttribution());

      const pendingToken = sessionStorage.getItem('pendingInviteToken');
      const goNext = () => {
        if (pendingToken) {
          sessionStorage.removeItem('pendingInviteToken');
          navigate(`/accept-invite?token=${pendingToken}`);
          return;
        }

        // Google accounts are pre-verified, but check anyway
        if (!userCredential.user.emailVerified) {
          navigate('/verify-email');
        } else if (redirectPath) {
          navigate(redirectPath);
        } else {
          navigate('/dashboard');
        }
      };

      if (isNewUser && !pendingToken) {
        fireSignupConversion(userCredential.user.email, goNext);
      } else {
        goNext();
      }
    } catch (err) {
      setError(err.message || 'Failed to log in with Google');
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword(e) {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address first');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await resetPassword(email);
      setResetSent(true);
    } catch (err) {
      const msg = err?.message || '';
      if (msg.includes('429') || /too many/i.test(msg)) {
        setError('Too many reset attempts. Please wait and try again.');
      } else {
        setError('Failed to send reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-wrap">
        <a href="https://gocta.ai" className="auth-wordmark">gocta<span className="accent">.ai</span></a>
        <div className="auth-card">
          {error && (
            <div className="auth-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="auth-field">
              <label className="auth-label">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="auth-input"
              />
            </div>

            <div className="auth-field" style={{ marginBottom: '0.5rem' }}>
              <label className="auth-label">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="auth-input"
              />
            </div>
            <div className="auth-forgot-row">
              <button
                type="button"
                onClick={handleForgotPassword}
                className="auth-forgot-btn"
              >
                Forgot password?
              </button>
            </div>
            {resetSent && (
              <div className="auth-success">
                Password reset email sent to <strong>{email}</strong>. Check your inbox (and spam folder).
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="auth-btn-primary"
            >
              {loading ? 'Logging in...' : 'Log In'}
            </button>
          </form>

          {/* Google Sign-In Button - Official Design */}
          <button
            onClick={handleGoogleLogin}
            disabled={loading}
            className="auth-btn-google"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
              <g fill="#000" fillRule="evenodd">
                <path d="M9 3.48c1.69 0 2.83.73 3.48 1.34l2.54-2.48C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l2.91 2.26C4.6 5.05 6.62 3.48 9 3.48z" fill="#EA4335"/>
                <path d="M17.64 9.2c0-.74-.06-1.28-.19-1.84H9v3.34h4.96c-.21 1.18-.84 2.18-1.79 2.91l2.73 2.12c1.9-1.75 3-4.33 3-7.53z" fill="#4285F4"/>
                <path d="M3.88 10.78A5.54 5.54 0 0 1 3.58 9c0-.62.11-1.22.29-1.78L.96 4.96A9.008 9.008 0 0 0 0 9c0 1.45.35 2.82.96 4.04l2.92-2.26z" fill="#FBBC05"/>
                <path d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.73-2.12c-.76.53-1.78.9-3.23.9-2.38 0-4.4-1.57-5.12-3.74L.96 13.04C2.45 15.98 5.48 18 9 18z" fill="#34A853"/>
              </g>
            </svg>
            <span>Sign in with Google</span>
          </button>

          <div className="auth-footer">
            <Link to="/signup" className="auth-link">
              Don't have an account? Sign up
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
